import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import {
  Drawer,
  ErrorBanner,
  InputField,
  SubmitButton,
  toast,
} from '@saas-kit/ui';

import type { CreateUserPayload } from './api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (payload: CreateUserPayload) => Promise<void>;
}

export function UserCreateForm({
  open,
  onOpenChange,
  isPending,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('adminUsers');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setEmail('');
    setPassword('');
    setFullName('');
    setIsSuperuser(false);
    setError(null);
  }

  function handleClose(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (isPending) return;
    setError(null);

    const payload: CreateUserPayload = {
      email: email.trim(),
      password,
      is_superuser: isSuperuser,
    };
    const trimmedName = fullName.trim();
    if (trimmedName) {
      payload.full_name = trimmedName;
    }

    void (async () => {
      try {
        await onSubmit(payload);
        toast.success(t('toasts.created'));
        reset();
        onOpenChange(false);
      } catch (err) {
        setError(parseApiError(err).message);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleClose}
      title={t('create.title')}
      description={t('create.description')}
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <InputField
          label={t('create.email_label')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          required
        />
        <InputField
          label={t('create.password_label')}
          hint={t('create.password_hint')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={8}
          required
        />
        <InputField
          label={t('create.full_name_label')}
          hint={t('create.full_name_hint')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={isSuperuser}
            onChange={(e) => setIsSuperuser(e.target.checked)}
          />
          <span>
            <span className="font-medium text-foreground">
              {t('create.is_superuser_label')}
            </span>
            <span className="block text-muted-foreground">
              {t('create.is_superuser_hint')}
            </span>
          </span>
        </label>

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <div className="pt-2">
          <SubmitButton
            loading={isPending}
            label={t('create.submit')}
            loadingLabel={t('create.submitting')}
          />
        </div>
      </form>
    </Drawer>
  );
}
